import type { PlainMessage } from '@bufbuild/protobuf';
import * as RadixPopover from '@radix-ui/react-popover';
import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  NoteDentition,
  NoteTableCellType,
  NoteTeethGranularity,
  type NoteTableBlock,
  type NoteTableColumn,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type { NoteToothNotation } from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';
import { Icon } from '@/shared/ui';

import {
  addNoteTableRow,
  emptyNoteTableCell,
  formatNoteDecimal,
  formatNoteTooth,
  listNoteTableRows,
  noteTableCellKey,
  parseNoteDecimal,
  printNoteDate,
  printNoteTable,
  printNoteUnit,
  setNoteTableCell,
  toNoteTableChange,
  type NoteTableRows,
  type SurfaceBlock,
} from '../lib';

import { AssumptionActions } from './AssumptionActions';
import { NoteDatePicker } from './NoteDatePicker';
import styles from './NoteTableField.module.scss';
import { NoteTeethPicker } from './NoteTeethPicker';
import type { LineMode } from './TemplateFieldView';
import fieldStyles from './TemplateFieldView.module.scss';

type Column = PlainMessage<NoteTableColumn>;

// One tooth of any dentition, no shortcuts.
const ONE_TOOTH = {
  Dentition: NoteDentition.NoteDentitionMixed,
  Teeth: [],
  Groups: [],
  Multiple: false,
  Granularity: NoteTeethGranularity.NoteTeethGranularityInvalidValue,
  Codes: [],
};

type CellInputProps = {
  value: string;
  label: string;
  className?: string;
  // The typed text as stored, or null when the cell does not take it.
  parse: (text: string) => string | null;
  format: (value: string) => string;
  onCommit: (value: string) => void;
};

// A text or number cell: Enter or leaving it applies the text, Escape
// keeps what the cell held; a text the cell does not take is marked and not
// applied.
const CellInput: FC<CellInputProps> = ({
  value,
  label,
  className,
  parse,
  format,
  onCommit,
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? format(value);
  const parsed = parse(text.trim());
  const apply = () => {
    if (draft === null || parsed === null) {
      return;
    }
    setDraft(null);
    if (parsed !== value) {
      onCommit(parsed);
    }
  };

  return (
    <input
      className={cn(
        'p2',
        fieldStyles.valueInput,
        styles.cellInput,
        parsed === null && styles.refused,
        className,
      )}
      aria-label={label}
      aria-invalid={parsed === null}
      value={text}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={apply}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          apply();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          setDraft(null);
        }
      }}
    />
  );
};

type CellProps = {
  column: Column;
  value: string;
  label: string;
  notation: NoteToothNotation | undefined;
  language?: string;
  onCommit: (value: string) => void;
};

// One cell by its column's type: a box to tick, the calendar, a text, a
// number with its unit, or the teeth picker for one tooth.
const Cell: FC<CellProps> = ({
  column,
  value,
  label,
  notation,
  language,
  onCommit,
}) => {
  const [open, setOpen] = useState(false);
  switch (column.Type) {
    case NoteTableCellType.NoteTableCellTypeCheckbox:
      return (
        <input
          type="checkbox"
          className={styles.checkbox}
          aria-label={label}
          checked={value === 'true'}
          onChange={(event) =>
            onCommit(event.target.checked ? 'true' : 'false')
          }
        />
      );
    case NoteTableCellType.NoteTableCellTypeDate:
      return open ? (
        <NoteDatePicker
          value={value}
          onClose={(next) => {
            setOpen(false);
            if (next !== undefined) {
              onCommit(next);
            }
          }}
        />
      ) : (
        <button
          type="button"
          className={cn('p2', styles.cellButton)}
          aria-label={label}
          onClick={() => setOpen(true)}
        >
          {value ? printNoteDate(value, language) : <EmptyCell />}
        </button>
      );
    case NoteTableCellType.NoteTableCellTypeNumber: {
      const unit = printNoteUnit(column.Unit, language);
      return (
        <span className={styles.number}>
          <CellInput
            value={value}
            label={label}
            className={styles.numberInput}
            parse={(text) =>
              text ? parseNoteDecimal(text, column.Precision ?? 0) : ''
            }
            format={(stored) => formatNoteDecimal(stored, language)}
            onCommit={onCommit}
          />
          {unit && <span className="p2">{unit}</span>}
        </span>
      );
    }
    case NoteTableCellType.NoteTableCellTypeTooth:
      return (
        <RadixPopover.Root open={open} onOpenChange={setOpen}>
          <RadixPopover.Trigger asChild>
            <button
              type="button"
              className={cn('p2', styles.cellButton)}
              aria-label={label}
            >
              {value ? formatNoteTooth(value, notation) : <EmptyCell />}
            </button>
          </RadixPopover.Trigger>
          <RadixPopover.Content
            className={cn(fieldStyles.popover, fieldStyles.widePopover)}
            side="bottom"
            align="start"
            sideOffset={6}
            collisionPadding={16}
            onEscapeKeyDown={(event) => event.stopPropagation()}
          >
            <p className={cn('p4', fieldStyles.popoverTitle)}>{label}</p>
            <NoteTeethPicker
              block={ONE_TOOTH}
              teeth={value ? [value] : []}
              groups={[]}
              notation={notation}
              multiple={false}
              restrictToBlock
              teethOnly
              onChange={([tooth = '']) => {
                setOpen(false);
                onCommit(tooth);
              }}
            />
          </RadixPopover.Content>
        </RadixPopover.Root>
      );
    default:
      return (
        <CellInput
          value={value}
          label={label}
          parse={(text) => text}
          format={(stored) => stored}
          onCommit={onCommit}
        />
      );
  }
};

const EmptyCell = () => <span aria-hidden>—</span>;

type Props = {
  block: SurfaceBlock;
  def: PlainMessage<NoteTableBlock>;
  mode: LineMode;
  editable: boolean;
  approved: boolean;
  notation: NoteToothNotation | undefined;
  language?: string;
  // The whole table; null clears the field.
  onChange: (rows: NoteTableRows | null) => void;
};

// A table field: rows down, columns across, each cell by its column's type.
// A change of a cell sends the whole table, one step to undo. A table whose
// rows the note adds takes "+ row" and "× row"; a row added stays here,
// unsent, until one of its cells holds something. A cell AI set reads as
// AI's, one it assumed or suggested underlined until it is confirmed.
export const NoteTableField: FC<Props> = ({
  block,
  def,
  mode,
  editable,
  approved,
  notation,
  language,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  // Rows added here and not filled yet.
  const [added, setAdded] = useState(0);
  const label =
    def.Label ??
    formatMessage({
      id: 'workspace.note.table',
      defaultMessage: 'Table',
    });

  if (mode === 'template') {
    return (
      <span className={cn('p2', fieldStyles.valueMark)}>
        <Icon name="layoutThreeColumns" size={14} />
        <span>{label}</span>
      </span>
    );
  }
  const stored = listNoteTableRows(def, block.selection.tableRows ?? []);
  if (!editable || approved) {
    const printed =
      block.text || printNoteTable(def, stored, { language, notation });
    return (
      <span
        className={cn(
          'p2',
          printed ? fieldStyles.choiceText : fieldStyles.valueMark,
        )}
      >
        {printed || label}
      </span>
    );
  }

  let rows = stored;
  for (let index = 0; index < added; index += 1) {
    rows = addNoteTableRow(def, rows);
  }
  const rowLabel = (index: number) =>
    def.Rows.find((fixed) => fixed.Code === rows[index].Row)?.Label ??
    formatMessage(
      { id: 'workspace.note.tableRow', defaultMessage: 'Row {number}' },
      { number: index + 1 },
    );
  const send = (next: NoteTableRows) => onChange(toNoteTableChange(def, next));
  const commit = (rowIndex: number, column: string, value: string) => {
    if (rowIndex >= stored.length) {
      setAdded((count) => count - 1);
    }
    send(setNoteTableCell(rows, rowIndex, column, value));
  };
  const removeRow = (rowIndex: number) => {
    if (rowIndex >= stored.length) {
      setAdded((count) => count - 1);
      return;
    }
    send(stored.filter((_, index) => index !== rowIndex));
  };

  return (
    <span className={styles.field} data-component="workspace-note-table">
      <table className={styles.table} aria-label={label}>
        <thead>
          <tr>
            <td />
            {def.Columns.map((column) => (
              <th
                key={column.Code}
                scope="col"
                className={cn('p2', styles.columnHead)}
              >
                {column.Label}
              </th>
            ))}
            {def.AddRows && <td />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={row.Row || `added-${rowIndex}`}>
              <th scope="row" className={cn('p2', styles.rowHead)}>
                {rowLabel(rowIndex)}
              </th>
              {def.Columns.map((column, columnIndex) => {
                const key = noteTableCellKey(row.Row, column.Code);
                const ai = block.aiCells?.[key];
                return (
                  <td
                    key={column.Code}
                    className={cn(
                      styles.cell,
                      ai && styles.aiCell,
                      ai?.unconfirmed && styles.assumedCell,
                    )}
                    title={ai?.quote || undefined}
                    data-cell={key}
                  >
                    <Cell
                      column={column}
                      value={row.Cells[columnIndex]?.Value ?? ''}
                      label={`${rowLabel(rowIndex)}: ${column.Label}`}
                      notation={notation}
                      language={language}
                      onCommit={(value) => commit(rowIndex, column.Code, value)}
                    />
                    {ai?.unconfirmed && (
                      <AssumptionActions
                        confirmLabel={formatMessage({
                          id: 'workspace.note.confirmFieldParts',
                          defaultMessage: "Confirm all the field's suggestions",
                        })}
                        rejectLabel={formatMessage(
                          {
                            id: 'workspace.note.rejectAssumedPart',
                            defaultMessage:
                              'Remove “{value}”, which AI assumed',
                          },
                          { value: `${rowLabel(rowIndex)}: ${column.Label}` },
                        )}
                        // The table set as it is confirms what it keeps.
                        onConfirm={() => send(stored)}
                        onReject={() =>
                          commit(
                            rowIndex,
                            column.Code,
                            emptyNoteTableCell(column),
                          )
                        }
                      />
                    )}
                  </td>
                );
              })}
              {def.AddRows && (
                <td className={styles.rowTools}>
                  <button
                    type="button"
                    className={fieldStyles.clearValue}
                    aria-label={formatMessage(
                      {
                        id: 'workspace.note.tableRemoveRow',
                        defaultMessage: 'Remove the row “{row}”',
                      },
                      { row: rowLabel(rowIndex) },
                    )}
                    onClick={() => removeRow(rowIndex)}
                  >
                    <Icon name="close" size={10} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {def.AddRows && (
        <button
          type="button"
          className={cn('p2', styles.addRow)}
          onClick={() => setAdded((count) => count + 1)}
        >
          <FormattedMessage
            id="workspace.note.tableAddRow"
            defaultMessage="+ row"
          />
        </button>
      )}
    </span>
  );
};
